import React, { FC } from "react";
import { Box, Text } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { BaseDialog } from "@getregistered/greg-editors/dist/lib/primer-ui/BaseDialog/BaseDialog";
import {
  BulkArchiveResult,
  BulkArchiveRow,
  BulkArchiveStatus,
  isRefusal,
} from "./bulkArchiveFlow";

interface ArchiveConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectionCount: number;
  // Null until the request comes back. Once set, the dialog switches from
  // asking to reporting, because a bulk archive is rarely all-or-nothing and
  // the rows that did not archive are the ones worth reading.
  result: BulkArchiveResult | null;
  isSubmitting: boolean;
}

const STATUS_LABELS: Record<BulkArchiveStatus, string> = {
  archived: "Archived",
  skipped: "Already archived",
  refused: "Landing sites, archive these from Landing Sites",
  unauthorized: "You do not have permission to archive these",
  not_found: "No longer available",
  failed: "Could not be archived",
};

const REPORTED_STATUSES: BulkArchiveStatus[] = [
  "skipped",
  "refused",
  "unauthorized",
  "not_found",
  "failed",
];

const eventNoun = (count: number) => (count === 1 ? "event" : "events");

const RowList: FC<{ rows: BulkArchiveRow[] }> = ({ rows }) => (
  <Box as="ul" sx={{ pl: 4, m: 0 }}>
    {rows.map((row) => (
      <Box as="li" key={row.id} sx={{ fontSize: 0 }}>
        {row.title || `Event #${row.id}`}
        {row.reason ? `: ${row.reason}` : ""}
      </Box>
    ))}
  </Box>
);

export const ArchiveConfirmationModal: FC<ArchiveConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectionCount,
  result,
  isSubmitting,
}) => {
  const refused = result !== null && isRefusal(result);
  const report = result !== null && !isRefusal(result) ? result : null;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Archive Events"
      position="center"
      width="600px"
      leftButtonText={result ? "Close" : "Go back"}
      rightButtonText={result ? "Done" : `Archive ${selectionCount} ${eventNoun(selectionCount)}`}
      rightButtonVariant={result ? "primary" : "danger"}
      rightButtonDisabled={isSubmitting || selectionCount === 0}
      onLeftButtonClick={onClose}
      onRightButtonClick={result ? onClose : onConfirm}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {refused && (
          <Banner variant="critical" title={(result as { errors: string[] }).errors.join(" ")} />
        )}

        {report && (
          <>
            <Text as="div" fontSize={1}>
              <Text as="span" fontWeight="bold">
                {report.counts.archived} {eventNoun(report.counts.archived)}
              </Text>{" "}
              archived.
            </Text>

            {REPORTED_STATUSES.filter((status) => report.counts[status] > 0).map((status) => (
              <Box key={status} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Text as="div" fontSize={0} fontWeight="bold">
                  {STATUS_LABELS[status]} ({report.counts[status]})
                </Text>
                <RowList rows={report.results.filter((row) => row.status === status)} />
              </Box>
            ))}
          </>
        )}

        {!result && (
          <>
            <Text as="div" fontSize={1}>
              Archiving moves{" "}
              <Text as="span" fontWeight="bold">
                {selectionCount} {eventNoun(selectionCount)}
              </Text>{" "}
              off the events list and into the Archived tab.
            </Text>
            <Text as="div" fontSize={1}>
              Nothing is deleted, and an archived event can be unarchived again from the
              Archived tab. Events you do not have permission to archive, and ones already
              archived, are reported back rather than changed.
            </Text>
            {/* GR-4399: archiving cancels a converted event's request. */}
            <Text as="div" fontSize={1}>
              An event created from an event request also cancels that request and releases its
              facility bookings. Unarchiving does not restore either.
            </Text>
          </>
        )}
      </Box>
    </BaseDialog>
  );
};
